// Firebase client initialization (web SDK)
// Values are expected in environment variables prefixed with REACT_APP_
// See .env.example at repo root.
import { initializeApp, getApps } from 'firebase/app';
import { getAuth, GoogleAuthProvider } from 'firebase/auth';
import { initializeFirestore, memoryLocalCache } from 'firebase/firestore';
import { getStorage } from 'firebase/storage';
import { getMessaging, getToken, onMessage, isSupported, Messaging } from 'firebase/messaging';

const firebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY as string,
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN as string,
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID as string,
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET as string,
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID as string,
  appId: process.env.REACT_APP_FIREBASE_APP_ID as string,
  measurementId: process.env.REACT_APP_FIREBASE_MEASUREMENT_ID as string | undefined,
};

// Guard against re-init in HMR
const app = getApps().length ? getApps()[0] : initializeApp(firebaseConfig);

export const auth = getAuth(app);
export const googleProvider = new GoogleAuthProvider();

// Initialize Firestore with stable transport settings to prevent INTERNAL ASSERTION on some networks
// Default: force long polling ON for reliability; can be overridden via env
const FS_FORCE_LP = (process.env.REACT_APP_FS_FORCE_LP || 'true').toLowerCase() === 'true';
const FS_AUTODETECT_LP = (process.env.REACT_APP_FS_AUTODETECT_LP || (FS_FORCE_LP ? 'false' : 'true')).toLowerCase() === 'true';

export const db = initializeFirestore(app, {
  experimentalAutoDetectLongPolling: FS_AUTODETECT_LP,
  experimentalForceLongPolling: FS_FORCE_LP,
  // Avoid IndexedDB persistence issues that can trigger INTERNAL ASSERTION on some setups
  localCache: memoryLocalCache(),
});

// Better error handling for Firestore
if (process.env.NODE_ENV === 'development') {
  console.log('🔥 Firebase initialized for development');

  const AUTO_RELOAD_ON_ASSERT = (process.env.REACT_APP_FIREBASE_AUTO_RELOAD || 'false').toLowerCase() === 'true';
  // Global Firestore error handler (dev only)
  window.addEventListener('unhandledrejection', (event) => {
    const msg: string | undefined = event?.reason?.message;
    if (msg && msg.includes('FIRESTORE') && msg.includes('INTERNAL ASSERTION')) {
      console.error('🚨 Firestore INTERNAL ASSERTION detected', {
        message: event.reason.message,
        code: event.reason.code,
        stack: event.reason.stack,
      });

      event.preventDefault();

      const alreadyHandled = sessionStorage.getItem('fb-assert-shown') === '1';
      if (!alreadyHandled && AUTO_RELOAD_ON_ASSERT) {
        sessionStorage.setItem('fb-assert-shown', '1');
        setTimeout(() => window.location.reload(), 2000);
      }
    }
  });
}

// Storage with optimized settings
export const storage = getStorage(app);

// Firebase Cloud Messaging (Push Notifications)
let messaging: Messaging | null = null;

// Initialize messaging only if supported (not in Node.js, service workers, etc.)
export const initializeMessaging = async (): Promise<Messaging | null> => {
  try {
    const supported = await isSupported();
    if (supported && !messaging) {
      messaging = getMessaging(app);
    }
    return messaging;
  } catch (error) {
    console.warn('Firebase Messaging not supported:', error);
    return null;
  }
};

// Get FCM token for push notifications
export const getFCMToken = async (): Promise<string | null> => {
  try {
    const msg = await initializeMessaging();
    if (!msg) return null;
    
    const vapidKey = process.env.REACT_APP_FIREBASE_VAPID_KEY;
    if (!vapidKey) {
      console.warn('VAPID key not configured for push notifications');
      return null;
    }
    
    const token = await getToken(msg, { vapidKey });
    return token;
  } catch (error) {
    console.error('Error getting FCM token:', error);
    return null;
  }
};

// Listen for foreground messages
export const onForegroundMessage = (callback: (payload: any) => void) => {
  initializeMessaging().then((msg) => {
    if (msg) {
      onMessage(msg, callback);
    }
  });
};

// Storage helper: Generate optimized URLs with cache headers
export const getOptimizedStorageUrl = (url: string): string => {
  if (!url || !url.includes('firebasestorage.googleapis.com')) {
    return url;
  }
  
  try {
    const urlObj = new URL(url);
    // Add cache control (24 hours)
    urlObj.searchParams.set('cache', '86400');
    return urlObj.toString();
  } catch {
    return url;
  }
};

export default app;
